import { useState, useEffect } from 'react';

/**
 * Hook to detect if media query matches
 * @param query - CSS media query string (e.g., "(min-width: 768px)")
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Modern API (addEventListener)
    mediaQueryList.addEventListener('change', handleChange);

    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

// Predefined breakpoints matching Tailwind defaults
const breakpoints = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
} as const;

type Breakpoint = keyof typeof breakpoints;

/**
 * Hook to detect if screen is at or above a breakpoint
 * @param breakpoint - Tailwind breakpoint name
 * @returns boolean indicating if screen is at or above the breakpoint
 */
export function useBreakpoint(breakpoint: Breakpoint): boolean {
  return useMediaQuery(breakpoints[breakpoint]);
}

/**
 * Hook to detect if device is mobile (below md breakpoint)
 * @returns boolean indicating if device is mobile
 */
export function useIsMobile(): boolean {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  return !isDesktop;
}

/**
 * Hook to detect mobile + landscape orientation. Usado pelo Editor pra
 * promover o layout desktop (toolbar vertical + side panel) quando o
 * usuário vira o telefone — ganho enorme de espaço usável vs portrait.
 *
 * IMPORTANTE: em phone-landscape a WIDTH passa de 768px (ex iPhone 14 =
 * 844×390 deitado), então `useIsMobile` (width-based) ZERA e nunca daria
 * match. Detectamos pela ALTURA: phones em landscape têm height ≤ ~500px
 * (iPhone SE = 320, Pixel 7 = 412, iPhone 14 = 390); tablets em landscape
 * começam em ~700px (iPad mini = 768). Combinado com orientation:landscape
 * isolamos a classe "telefone deitado" sem confundir com tablet/desktop.
 */
export function useIsMobileLandscape(): boolean {
  return useMediaQuery('(orientation: landscape) and (max-height: 500px)');
}

/**
 * Hook to detect mobile + portrait. Usado pra mostrar bottom dock só
 * em portrait — em landscape o user prefere ver o preview maximizado
 * com tools laterais (mesmo padrão do CapCut/InShot horizontais).
 */
export function useIsMobilePortrait(): boolean {
  const isMobile = useIsMobile();
  const isLandscape = useMediaQuery('(orientation: landscape)');
  return isMobile && !isLandscape;
}

/**
 * Hook to detect if device is desktop (md breakpoint and above)
 * @returns boolean indicating if device is desktop
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}

/**
 * Hook to get current breakpoint name
 * @returns current breakpoint name or null if below sm
 */
export function useCurrentBreakpoint(): Breakpoint | null {
  const isSm = useMediaQuery(breakpoints.sm);
  const isMd = useMediaQuery(breakpoints.md);
  const isLg = useMediaQuery(breakpoints.lg);
  const isXl = useMediaQuery(breakpoints.xl);
  const is2xl = useMediaQuery(breakpoints['2xl']);

  if (is2xl) return '2xl';
  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  return null;
}

/**
 * Hook to detect if user prefers reduced motion
 * @returns boolean indicating if user prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * Hook to detect if user prefers dark mode
 * @returns boolean indicating if user prefers dark mode
 */
export function usePrefersDarkMode(): boolean {
  return useMediaQuery('(prefers-color-scheme: dark)');
}
